import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Building2,
  Camera,
  CheckCircle2,
  IdCard,
  Loader2,
  Lock,
  Mail,
  Phone,
  Shield,
  Star,
  User,
  Users2,
  Activity,
  Zap,
} from 'lucide-react';
import { GamificationBadge } from '../../../components/ui/GamificationBadge';
import { useGamification } from '../../../hooks/useGamification';
import { supabase } from '../../../lib/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useAppToast } from '../../../components/AppToast';
import {
  ModuleCard,
  ModuleErrorState,
  ModuleLoadingState,
} from '../../shared/components/ModulePageHeader';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
type ProfileData = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  position?: string | null;
  company?: string | null;
  department?: string | null;
  avatar_url?: string | null;
  updated_at?: string | null;
};

type EditableField = 'name' | 'phone' | 'position' | 'company' | 'department';
type FieldStatus = 'idle' | 'saving' | 'saved' | 'error';

const ROLE_LABELS: Record<string, string> = {
  consultor_master: 'Consultor Master',
  consultant: 'Consultor',
  admin: 'Admin',
  manager: 'Gestor',
  gestor: 'Gestor',
  lead: 'Coordenador',
  coordenador: 'Coordenador',
  operator: 'Usuário',
  usuario: 'Usuário',
};

// ===========================================================================
// Página
// ===========================================================================
export const AccountProfilePage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const { showToast, ToastContainer } = useAppToast();

  const { userPoints, loading: gamLoading } = useGamification();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');

  const initialRef = useRef<Record<EditableField, string>>({
    name: '',
    phone: '',
    position: '',
    company: '',
    department: '',
  });

  const [draft, setDraft] = useState<Record<EditableField, string>>({
    name: '',
    phone: '',
    position: '',
    company: '',
    department: '',
  });
  const [status, setStatus] = useState<Record<EditableField, FieldStatus>>({
    name: 'idle',
    phone: 'idle',
    position: 'idle',
    company: 'idle',
    department: 'idle',
  });

  // -------------------------------------------------------------------------
  const profileQuery = useQuery({
    queryKey: ['account-profile', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<ProfileData | null> => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('user_profiles')
        .select('name, email, phone, position, company, department, avatar_url, updated_at')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    const data = profileQuery.data;
    const next: Record<EditableField, string> = {
      name: data?.name ?? user?.name ?? '',
      phone: data?.phone ?? '',
      position: data?.position ?? '',
      company: data?.company ?? '',
      department: data?.department ?? '',
    };
    setDraft(next);
    initialRef.current = { ...next };
    setPreviewImage(data?.avatar_url ?? '');
  }, [profileQuery.data, user?.name]);

  const emailValue = useMemo(
    () => profileQuery.data?.email || user?.email || '',
    [profileQuery.data?.email, user?.email],
  );

  const roleLabel = useMemo(() => {
    const role = (workspace?.userRole || user?.role || '').toString();
    return ROLE_LABELS[role] || 'Usuário';
  }, [workspace?.userRole, user?.role]);

  // -------------------------------------------------------------------------
  // Avatar: redimensiona client-side, salva como data URL em user_profiles
  // e em auth.user_metadata. Não depende de bucket de Storage.
  // -------------------------------------------------------------------------
  const resizeImageToDataUrl = (file: File, maxSize = 256, quality = 0.85): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Falha ao ler imagem'));
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('Imagem inválida'));
        img.onload = () => {
          const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('Canvas indisponível'));
          ctx.drawImage(img, 0, 0, w, h);
          try {
            resolve(canvas.toDataURL('image/jpeg', quality));
          } catch (e) {
            reject(e);
          }
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    if (!file.type.startsWith('image/')) {
      showToast('warning', 'Selecione um arquivo de imagem.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('warning', 'Imagem muito grande (máx. 5MB).');
      return;
    }

    setAvatarUploading(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 256, 0.85);
      setPreviewImage(dataUrl);

      const { error: upsertError } = await supabase
        .from('user_profiles')
        .upsert(
          { user_id: user.id, avatar_url: dataUrl, updated_at: new Date().toISOString() },
          { onConflict: 'user_id' },
        );
      if (upsertError) throw upsertError;

      await supabase.auth.updateUser({ data: { avatar_url: dataUrl } });

      await queryClient.invalidateQueries({ queryKey: ['account-profile', user.id] });
      showToast('success', 'Foto de perfil atualizada.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao atualizar a foto.';
      console.error('[AccountProfile] upload avatar:', error);
      showToast('error', message);
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // -------------------------------------------------------------------------
  // Autosave inline por campo (onBlur)
  // -------------------------------------------------------------------------
  const saveField = async (field: EditableField) => {
    if (!user?.id) return;
    const value = (draft[field] || '').trim();
    if (value === initialRef.current[field]) return;

    setStatus((s) => ({ ...s, [field]: 'saving' }));
    try {
      const payload: Record<string, unknown> = {
        user_id: user.id,
        [field]: value || null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from('user_profiles')
        .upsert(payload, { onConflict: 'user_id' });
      if (error) throw error;

      if (field === 'name') {
        await supabase.auth.updateUser({ data: { name: value } });
      }

      initialRef.current[field] = value;
      setStatus((s) => ({ ...s, [field]: 'saved' }));
      window.setTimeout(() => {
        setStatus((s) => (s[field] === 'saved' ? { ...s, [field]: 'idle' } : s));
      }, 1800);

      queryClient.setQueryData<ProfileData | null>(
        ['account-profile', user.id],
        (prev) => ({ ...(prev || {}), [field]: value, updated_at: new Date().toISOString() }),
      );
    } catch (error) {
      console.error(`[AccountProfile] saveField(${field}):`, error);
      setStatus((s) => ({ ...s, [field]: 'error' }));
      showToast('error', `Não foi possível salvar ${field}.`);
    }
  };

  const updateDraft = (field: EditableField, value: string) => {
    setDraft((d) => ({ ...d, [field]: value }));
  };

  // -------------------------------------------------------------------------
  return (
    <div className="enterprise-page h-full overflow-y-auto">
      {profileQuery.isPending && <ModuleLoadingState label="Carregando perfil" />}
      {profileQuery.isError && (
        <ModuleErrorState
          message="Não foi possível carregar o perfil do usuário no momento."
          retry={profileQuery.refetch}
        />
      )}

      {/* HEADER — Avatar + Identidade + Badge de cargo */}
      <ModuleCard title="Identidade">
        <div className="flex items-start gap-6">
          {/* ── Left block: avatar + info ─────────────────── */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="relative shrink-0 group w-20 h-20">
            <div className="w-20 h-20 rounded-full border border-slate-100 bg-slate-50 flex items-center justify-center relative overflow-hidden dark:bg-slate-800 dark:border-slate-700">
              {previewImage ? (
                <img src={previewImage} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-semibold text-slate-400 dark:text-slate-500">
                  {(draft.name || user?.email || 'US').substring(0, 2).toUpperCase()}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarUploading}
              title="Alterar foto"
              className="absolute inset-0 rounded-full flex items-center justify-center bg-slate-900/45 text-white opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100 disabled:bg-slate-900/60 cursor-pointer"
            >
              {avatarUploading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Camera size={18} />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold enterprise-text-primary truncate">
              {draft.name || user?.name || 'Usuário'}
            </h2>
            <p className="text-xs enterprise-text-secondary truncate">{emailValue}</p>
            <span className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800">
              <Shield size={11} strokeWidth={2} />
              {roleLabel}
            </span>
          </div>
          </div>

          {/* ── Right block: gamification badge ──────────── */}
          {user?.id && (
            <div className="shrink-0 self-start">
              <GamificationBadge
                userId={user.id}
                variant="full"
                showProgress
              />
            </div>
          )}
        </div>
      </ModuleCard>

      {/* DADOS PESSOAIS — autosave inline */}
      <ModuleCard title="Dados Pessoais">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InlineField
            label="Nome Completo"
            icon={<User size={14} />}
            value={draft.name}
            placeholder="Seu nome completo"
            status={status.name}
            onChange={(v) => updateDraft('name', v)}
            onBlur={() => saveField('name')}
          />
          <InlineField
            label="Cargo"
            icon={<Briefcase size={14} />}
            value={draft.position}
            placeholder="Ex: Consultor Lean"
            status={status.position}
            onChange={(v) => updateDraft('position', v)}
            onBlur={() => saveField('position')}
          />
          <InlineField
            label="Empresa"
            icon={<Building2 size={14} />}
            value={draft.company}
            placeholder="Nome da empresa"
            status={status.company}
            onChange={(v) => updateDraft('company', v)}
            onBlur={() => saveField('company')}
          />
          <InlineField
            label="Telefone"
            icon={<Phone size={14} />}
            value={draft.phone}
            placeholder="(11) 99999-9999"
            status={status.phone}
            onChange={(v) => updateDraft('phone', v)}
            onBlur={() => saveField('phone')}
          />
          <InlineField
            label="Departamento"
            icon={<Building2 size={14} />}
            value={draft.department}
            placeholder="Ex: Excelência Operacional"
            status={status.department}
            onChange={(v) => updateDraft('department', v)}
            onBlur={() => saveField('department')}
          />
          <InlineField
            label="E-mail"
            icon={<Mail size={14} />}
            value={emailValue}
            placeholder="email@empresa.com"
            disabled
            disabledHint="Credencial bloqueada"
            type="email"
          />
          <InlineField
            label="CPF"
            icon={<IdCard size={14} />}
            value=""
            placeholder="Não cadastrado"
            disabled
            disabledHint="Credencial bloqueada"
          />
        </div>
        <p className="mt-3 text-[11px] enterprise-text-secondary">
          As alterações são salvas automaticamente ao sair do campo.
        </p>
      </ModuleCard>

      {/* INDICADORES DE GAMIFICAÇÃO */}
      <ModuleCard
        title="Desempenho Operacional"
        action={<Star size={14} className="text-amber-500" />}
      >
        {gamLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="enterprise-card-surface enterprise-surface-2 p-3 animate-pulse">
                <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                <div className="h-5 w-10 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

            {/* Pontuação Geral */}
            <GamScorecard
              icon={<Star size={14} className="text-amber-500" />}
              label="Pontuação Geral"
            >
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-[18px] font-bold enterprise-text-primary">
                  {(userPoints?.total_points ?? 0).toLocaleString('pt-BR')}
                </span>
                <span className="text-[11px] text-[var(--text-muted)] font-medium">pts</span>
                {userPoints && userPoints.current_multiplier > 1 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    {userPoints.current_multiplier.toFixed(1)}x ativo
                  </span>
                )}
              </div>
            </GamScorecard>

            {/* Entregas Lean */}
            <GamScorecard
              icon={<Zap size={14} className="text-blue-500" />}
              label="Entregas Lean"
            >
              <div className="flex items-end gap-4 mt-0.5">
                <div>
                  <p className="text-[18px] font-bold enterprise-text-primary leading-none">
                    {userPoints?.kaizens_completed ?? 0}
                  </p>
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">kaizens</p>
                </div>
                <div>
                  <p className="text-[18px] font-bold enterprise-text-primary leading-none">
                    {userPoints?.projects_led ?? 0}
                  </p>
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">projetos</p>
                </div>
              </div>
            </GamScorecard>

            {/* Disciplina */}
            <GamScorecard
              icon={<Activity size={14} className="text-emerald-500" />}
              label="Disciplina"
            >
              <div className="flex items-baseline gap-1.5">
                <span className="text-[18px] font-bold enterprise-text-primary">
                  {(userPoints?.discipline_points ?? 0).toLocaleString('pt-BR')}
                </span>
                <span className="text-[11px] text-[var(--text-muted)] font-medium">pts</span>
              </div>
            </GamScorecard>

            {/* Colaboração */}
            <GamScorecard
              icon={<Users2 size={14} className="text-violet-500" />}
              label="Colaboração"
            >
              <div className="flex items-baseline gap-1.5">
                <span className="text-[18px] font-bold enterprise-text-primary">
                  {userPoints?.team_collaborations ?? 0}
                </span>
                <span className="text-[11px] text-[var(--text-muted)] font-medium">eventos</span>
              </div>
            </GamScorecard>

          </div>
        )}

        {!gamLoading && !userPoints && (
          <p className="text-[12px] text-[var(--text-muted)] italic mt-3">
            Nenhum dado de gamificação encontrado para esta organização.
          </p>
        )}
      </ModuleCard>

      <ToastContainer />
    </div>
  );
};

// ===========================================================================
// Subcomponentes
// ===========================================================================
interface InlineFieldProps {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  status?: FieldStatus;
  disabled?: boolean;
  disabledHint?: string;
  type?: string;
}

const InlineField: React.FC<InlineFieldProps> = ({
  label,
  icon,
  value,
  onChange,
  onBlur,
  placeholder,
  status = 'idle',
  disabled,
  disabledHint,
  type = 'text',
}) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="enterprise-field-label">{label}</label>
        {status === 'saving' && (
          <span className="flex items-center gap-1 text-[11px] font-medium text-slate-400">
            <Loader2 size={11} className="animate-spin" /> Salvando…
          </span>
        )}
        {status === 'saved' && (
          <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600">
            <CheckCircle2 size={11} /> Salvo
          </span>
        )}
        {status === 'error' && (
          <span className="text-[11px] font-medium text-rose-500">Falha ao salvar</span>
        )}
        {disabled && disabledHint && status === 'idle' && (
          <span className="flex items-center gap-1 text-[11px] font-medium text-slate-400">
            <Lock size={10} /> {disabledHint}
          </span>
        )}
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
          {icon}
        </span>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`enterprise-input w-full pl-9 pr-3 text-sm ${
            disabled ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        />
      </div>
    </div>
  );
};

const MetricTile: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono = false,
}) => (
  <div className="enterprise-card-surface enterprise-surface-2 p-3">
    <div className="enterprise-field-label">{label}</div>
    <div className={`mt-1 text-[15px] font-semibold enterprise-text-primary ${mono ? 'font-mono' : ''}`}>
      {value}
    </div>
  </div>
);

const GamScorecard: React.FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}> = ({ icon, label, children }) => (
  <div className="enterprise-card-surface enterprise-surface-2 p-3 flex flex-col gap-1.5">
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="enterprise-field-label leading-none">{label}</span>
    </div>
    <div>{children}</div>
  </div>
);
